import { translate } from '../i18n';
import type { Lang, RestaurantTable } from '../types';

/** Takeaway orders share one pseudo-table. */
export const PARCEL_TABLE = 'parcel';

/**
 * The floor plan that ships inside the app. Like the menu, this is only a
 * fallback: the live plan lives in `restaurant_tables` and is cached on the
 * device. It keeps a brand-new install usable before its first sync, and keeps
 * the till working if the cache is ever wiped while offline.
 *
 * These are the same eight tables plus Parcel that used to be hardcoded, so a
 * device that has never synced behaves exactly as it did before.
 */
export const FALLBACK_TABLES: RestaurantTable[] = [
  ...['1', '2', '3', '4', '5', '6', '7', '8'].map((id, i) => ({
    id,
    label: '',
    labelHi: '',
    labelGu: '',
    seats: 0,
    kind: 'dine_in' as const,
    sortOrder: (i + 1) * 10,
    active: true,
  })),
  {
    id: PARCEL_TABLE,
    label: '',
    labelHi: '',
    labelGu: '',
    seats: 0,
    kind: 'parcel' as const,
    sortOrder: 1000,
    active: true,
  },
];

export function isParcel(tableId: string): boolean {
  return tableId === PARCEL_TABLE;
}

export function sortTables(list: RestaurantTable[]): RestaurantTable[] {
  return [...list].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id, undefined, { numeric: true }),
  );
}

/**
 * What to call a table, in the staff member's language.
 *
 * A table carries a label only when it has a real name ("Garden 1"). Left
 * empty, a numeric id is rendered as "Table 4" / "टेबल 4" / "ટેબલ 4" through the
 * translations, which is why this cannot simply read the column.
 *
 * `table` may be undefined: an order placed months ago can name a table that
 * has since been deleted, and its receipt must still read correctly.
 */
export function tableLabel(
  lang: Lang,
  tableId: string,
  table?: RestaurantTable,
): string {
  if (table) {
    if (lang === 'hi' && table.labelHi) return table.labelHi;
    if (lang === 'gu' && table.labelGu) return table.labelGu;
    if (table.label) return table.label;
    if (table.kind === 'parcel') return translate(lang, 'parcel');
  }
  if (isParcel(tableId)) return translate(lang, 'parcel');
  if (/^\d+$/.test(tableId)) return `${translate(lang, 'table')} ${tableId}`;
  return tableId;
}
